import { Controller } from "@hotwired/stimulus"

// Advisory pre-submit warning for client (non-agent) users during office
// hours: if any chosen departure falls outside business hours (Mon-Fri
// 0800-1600 Brisbane, minus public holidays), the order will be submitted
// as Unconfirmed for ops review. The server's ClientOrderTimingPolicy is
// the source of truth — this only sets expectations. Times are read in the
// browser's local zone, which is Brisbane for this desktop-only user base.
export default class extends Controller {
  static targets = ["dialog"]
  static values = {
    active: Boolean,
    businessStart: Number,
    businessEnd: Number,
    holidays: Array,
    officeClosed: Boolean,
    nextOpening: String
  }

  intercept(event) {
    if (!this.activeValue || this._acknowledged) return
    if (!this.anyOutOfHoursDeparture()) return
    // Weeknight closure with a departure before the office reopens: the
    // server BLOCKS these outright, so promising "will be submitted as
    // Unconfirmed" here would mislead — let the submit through and the
    // server's error banner explain the block.
    if (this.officeClosedValue && this.anyDepartureBeforeNextOpening()) return
    // Same reasoning for vessel bookable-hours violations (QPP-1474): the
    // server hard-blocks them, so the time rule wins over this advisory —
    // skip the modal and let the rejection banner explain.
    if (this.anyVesselHoursViolation()) return

    event.preventDefault()
    this.dialogTarget.showModal()
  }

  anyVesselHoursViolation() {
    return !!this.element.querySelector('[data-vessel-hours-target="output"] .alert-warning')
  }

  anyDepartureBeforeNextOpening() {
    if (!this.nextOpeningValue) return false
    const nextOpening = new Date(this.nextOpeningValue)
    if (isNaN(nextOpening)) return false
    return this.departures().some((departure) => departure < nextOpening)
  }

  confirm() {
    this._acknowledged = true
    this.dialogTarget.close()
    this.element.requestSubmit()
  }

  cancel() {
    this.dialogTarget.close()
  }

  anyOutOfHoursDeparture() {
    const inputs = this.element.querySelectorAll('input[name$="[start_at]"]')
    return Array.from(inputs).some((input) => {
      if (!input.value) return false
      const departure = new Date(input.value)
      if (isNaN(departure)) return false
      const day = departure.getDay()
      if (day === 0 || day === 6) return true
      if (this.holidaysValue.includes(input.value.slice(0, 10))) return true
      const minutes = departure.getHours() * 60 + departure.getMinutes()
      return minutes < this.businessStartValue || minutes >= this.businessEndValue
    })
  }

  departures() {
    const inputs = this.element.querySelectorAll('input[name$="[start_at]"]')
    return Array.from(inputs)
      .filter((input) => input.value)
      .map((input) => new Date(input.value))
      .filter((departure) => !isNaN(departure))
  }
}
